<template>
  <Modal
    v-model:visible="isVisible"
    class="spot-market-request-template-modal"
    :header="t('common.saveParam', { param: t('templates.templates') })"
    @keyup.enter.stop="onSave"
    @keyup.esc.stop="isVisible = false"
  >
    <p id="template-instructions">
      {{
        !!template.id
          ? t('carrierPortal.spotMarket.templates.updateInstructions')
          : t('carrierPortal.spotMarket.templates.saveInstructions')
      }}
    </p>

    <form
      id="spot-market-request-template"
      aria-labelledby="template-instructions"
      @submit.prevent
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="spot-market-requeset-template-name"
        :errors="v$.name.$errors"
        :label="t('common.name')"
      >
        <InputText
          :id="inputId"
          v-model="internalValue.name"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          autofocus
          :placeholder="t('common.name')"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        v-if="hasUpdateOrCreateTemplatePermission"
        mode="primary"
        icon="pi pi-save"
        :label="saveButtonText"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { SpotMarketRequestTemplate } from '@rygen/rygen-ui/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: false,
});

const props = defineProps({
  template: {
    type: Object as PropType<SpotMarketRequestTemplate>,
    default: () => new SpotMarketRequestTemplate(),
  },
});
const { template } = toRefs(props);
const emit = defineEmits<{
  (event: 'save', name: string): void;
}>();

const { t } = useI18n();
const { uid } = useUid('save-order-template-modal');

const internalValue = ref({ name: template.value.name ?? '' });
const rules = {
  name: { required: required(t('common.name')) },
};
const { resetModel, v$, validate } = useValidations(internalValue, rules, uid);

const saveButtonText = computed(() => {
  return isSavingNewTemplate ? t('common.save') : t('common.saveAs');
});

const isSavingNewTemplate = computed(() => {
  return !template.value.id || template.value.name === internalValue.value.name;
});

const hasUpdateOrCreateTemplatePermission = computed(() => {
  if (isSavingNewTemplate) {
    return authorization.hasAuthority('spotMarketRequestTemplate', 'create');
  } else {
    return authorization.hasAuthority('spotMarketRequestTemplate', 'update');
  }
});

const onSave = async () => {
  if (!(await validate())) {
    return;
  }

  isVisible.value = false;
  emit('save', internalValue.value.name);
};

watch(
  template,
  (newVal: SpotMarketRequestTemplate) => {
    internalValue.value.name = newVal.name ?? '';
  },
  { immediate: true },
);

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    nextTick(() => resetModel(internalValue.value));
  }
});
</script>

<style>
.spot-market-request-template-modal {
  #spot-market-request-template {
    margin-top: var(--sp-3);
  }
}
</style>
